import { ListItem, Text } from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { fieldRouteConfig, insightsRouteConfig } from 'src/app/graph/routes';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import {
  NormalizedFieldsFieldUsage,
  NormalizedFieldsMetricsField,
  formatMetrics,
} from 'src/lib/metrics';

import { HighlightedListItemName } from './HighlightedListItemName';

interface FieldsProps extends NormalizedFieldsFieldUsage {
  name: string | null;
  signature?: string | null;
  statsSortKey?: NormalizedFieldsMetricsField;
  isSelected: boolean;
  hideStatsValue?: boolean;
  className?: string;
  children?: React.ReactNode;
  loading: boolean;
}

export const FieldsListItem = ({
  name,
  signature,
  statsSortKey,
  isSelected,
  hideStatsValue,
  children,
  className,
  loading,
  ...otherStatsProps
}: FieldsProps) => {
  const location = useLocation();
  const graphRef = useGraphRef();
  const { fieldSearch } = useRouteParams(insightsRouteConfig);

  const displayName = name || format.querySignature(signature);
  const splitName = displayName.split('.');
  const parentType = splitName[0] || ' ';
  const fieldName = splitName[1] || ' ';

  let statsDisplayValue: string | number | null | undefined =
    statsSortKey === 'alphabetically'
      ? otherStatsProps.requestsCount
      : statsSortKey && otherStatsProps[statsSortKey];

  if (statsSortKey && statsDisplayValue) {
    const splitMetrics = format.splitUnits(
      formatMetrics(statsSortKey, statsDisplayValue),
    );
    statsDisplayValue = `${splitMetrics.value}${splitMetrics.unit}`;
  }

  return (
    <ListItem isSelected={isSelected}>
      <Link
        to={{
          ...location,
          pathname: fieldRouteConfig.path({
            typeName: parentType,
            fieldName,
            graphId: graphRef?.graphId || '',
            graphVariant: graphRef?.graphVariant || '',
          }),
        }}
        className={classnames(className, 'flex w-full items-center truncate')}
      >
        <div className="mr-4 flex items-center truncate">
          <span title={displayName} className="flex flex-row truncate">
            <div className="truncate text-neutral">
              <HighlightedListItemName
                itemName={parentType}
                searchTerm={fieldSearch}
                loading={loading}
              />
            </div>
            <span>.</span>
            <span className="truncate font-semibold">
              <HighlightedListItemName
                itemName={fieldName}
                searchTerm={fieldSearch}
                loading={loading}
              />
            </span>
          </span>
        </div>
        <Text className="ml-auto text-secondary" variant="code" size="sm">
          {hideStatsValue
            ? null
            : statsDisplayValue === null || statsDisplayValue === undefined
              ? 'n/a'
              : statsDisplayValue}
        </Text>
      </Link>
    </ListItem>
  );
};
